import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useOnboarding } from "../../context/OnboardingContext";
import { useRBAC } from "../../context/RBACContext";
import { ApiHelper } from "../../utils/ApiHelper";
import { CREATE_USER, UPDATE_INVITE } from "../../lib/API_KEYS";

export default function InviteVerificationSuccess() {
  const navigate = useNavigate();
  const { inviteData } = useOnboarding();
  const [isProcessing, setIsProcessing] = useState(true);
  const hasRunRef = useRef(false);
  const { initializeRBAC } = useRBAC();

  useEffect(() => {
    if (!inviteData?.email || !inviteData?.password || hasRunRef.current) return;
    hasRunRef.current = true;

    const processInvite = async () => {
      try {
        console.log("Starting invite verification process...", inviteData);

        //Auto sign-in via Cognito
        const session = await Auth.signIn(inviteData.email, inviteData.password);

        // Load role & permissions immediately after login
        await initializeRBAC();

        const token = session.signInUserSession.idToken.jwtToken;
        const headers = { Authorization: `Bearer ${token}` };
        console.log("User signed in:", session);
        const payload = {
          email: inviteData.email,
          name: inviteData.fullName || "",
          profilePic: inviteData.profilePic || null,
          country: inviteData.country || null,
          city: inviteData.city || null,
          postcode: inviteData.postcode || null,
          role: inviteData.role,
          companyName: inviteData.companyName || "",
          companyId: inviteData.companyId,
          addedBy: inviteData.addedBy,
        };

        // 2️⃣ Save invited user in Users table
        await new ApiHelper().FetchFromServer(
          ...CREATE_USER,
          undefined,
          payload,
          headers
        );
        console.log("User saved in Users table");
        console.log("Invite Data:", inviteData);

        // Update invite status to accepted
        await new ApiHelper().FetchFromServer(
          UPDATE_INVITE[0].replace(":inviteId", inviteData?._id),
          UPDATE_INVITE[1],
          undefined, { status: 'accepted' }, headers
        );
        console.log("✅ Invite status updated");

        toast.success("Invite accepted and user created successfully!");
      } catch (err) {
        console.error("❌ Error processing invite:", err.response || err);
        toast.error("Failed to verify invite. Check console logs.");
      } finally {
        setIsProcessing(false);
      }
    };

    processInvite();
  }, [inviteData,]);

  const handleContinue = () => navigate("/preloader");

  return (
    <div className="d-flex vh-100">
      <div className="flex-grow-1 d-flex justify-content-center align-items-center">
        <div className="container-fluid px-3">
          <div className="row vh-100">
            <div className="col-12 d-flex justify-content-center align-items-center">
              <div className="text-center" style={{ maxWidth: "420px", width: "100%" }}>
                <div className="success-icon-svg mb-4 mx-auto">
                  <img src="/images/success.svg" alt="check_icon" />
                </div>

                <h4 className="fw-bold">
                  {isProcessing ? "Processing..." : "Verified"}
                </h4>

                <p className="text-muted mt-2 mb-4" style={{ fontSize: "14px" }}>
                  {isProcessing
                    ? "Saving user and updating invite..."
                    : "You have been successfully verified."}
                </p>

                {!isProcessing && (
                  <button
                    onClick={handleContinue}
                    className="btn btn-primary w-100"
                    style={{
                      backgroundColor: "#1A1A52",
                      fontSize: "16px",
                      fontWeight: "600",
                      height: "40px",
                      border: "none",
                    }}
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          </div>

          <style>{`
            .success-icon-svg {
              width: 64px;
              height: 64px;
              background-color: #ffffff;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 0 10px rgba(0,0,0,0.05);
            }
          `}</style>
        </div>
      </div>

      <div className="d-none d-md-flex align-items-center justify-content-center" style={{ width: "50%" }}>
        <img
          src="/images/BG.png"
          alt="Side"
          className="img-fluid"
          style={{ maxHeight: "90%", maxWidth: "90%", objectFit: "contain" }}
        />
      </div>

      <ToastContainer />
    </div>
  );
}
